import { useEffect, useState } from "react";
import { Globe2, X } from "lucide-react";
import { api, getMe } from "../lib/api";
import { lsGet, lsSet } from "../lib/safeStorage";

const LS_KEY = "habano.lang";

/**
 * Auto-detect browser language on first visit.
 *  • en → silently set, no popup
 *  • es → silently set, no popup
 *  • anything else → show selection popup
 * Show only once — never again after a language is chosen.
 * Mobile only.
 */
export default function LanguagePicker() {
  // Compute initial visibility synchronously so we never set state inside useEffect.
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    if (window.innerWidth >= 1024) return false; // mobile only
    const stored = lsGet(LS_KEY);
    if (stored === "en" || stored === "es") return false;
    const browser = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    if (browser.startsWith("en") || browser.startsWith("es")) return false;
    return true;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 1024) return;
    const stored = lsGet(LS_KEY);
    if (stored === "en" || stored === "es") return;
    const browser = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    const silentPick = (lang) => {
      lsSet(LS_KEY, lang);
      (async () => {
        try {
          const me = await getMe();
          if (me) await api.patch("/users/me", { language: lang }).catch(() => {});
        } catch { /* ignore */ }
      })();
    };
    if (browser.startsWith("en")) silentPick("en");
    else if (browser.startsWith("es")) silentPick("es");
  }, []);

  const persistLang = async (lang) => {
    lsSet(LS_KEY, lang);
    try {
      const me = await getMe();
      if (me) await api.patch("/users/me", { language: lang }).catch(() => {});
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("habano:lang", { detail: lang }));
  };
  const saveLang = (lang) => {
    setShow(false);
    persistLang(lang);
  };

  if (!show) return null;
  return (
    <div
      role="dialog"
      aria-label="Choose language"
      data-testid="lang-picker"
      className="fixed inset-0 z-[60] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl px-7 py-9 text-center"
        style={{
          background: "#171717",
          border: "1px solid rgba(232,184,75,0.28)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.03)",
        }}
      >
        <button
          type="button"
          onClick={() => saveLang("en")}
          data-testid="lang-picker-close"
          aria-label="Close"
          className="absolute top-4 right-4 grid place-items-center rounded-full hover:bg-white/[0.05]"
          style={{ width: 32, height: 32, color: "#B0AAA3" }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div className="flex justify-center mb-5">
          <span
            className="w-14 h-14 grid place-items-center rounded-full"
            style={{ background: "rgba(200,160,99,0.08)", border: "1px solid rgba(200,160,99,0.4)" }}
          >
            <Globe2 className="w-6 h-6 text-gold" strokeWidth={1.5} />
          </span>
        </div>
        <h2 className="font-display text-2xl leading-tight text-foreground">Choose your language</h2>
        <div className="font-display-italic text-gold text-base mt-1">Elige tu idioma</div>
        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            data-testid="lang-pick-en"
            onClick={() => saveLang("en")}
            className="btn-gold py-4 text-sm justify-center"
            style={{ minHeight: 56 }}
          >
            English
          </button>
          <button
            data-testid="lang-pick-es"
            onClick={() => saveLang("es")}
            className="btn-outline py-4 text-sm justify-center"
            style={{ minHeight: 56 }}
          >
            Español
          </button>
        </div>
      </div>
    </div>
  );
}
